import React, { useEffect, useMemo, useRef, useState } from 'react';
import Card from '../../../common/components/Card/Card';
import VersionContainer from './Version';
import Input from '../../../common/components/Input/Input';
import CustomSelect from '../../../common/components/CustomSelect/CustomSelect';
import './Contract.scss';
import Button from '../../../common/components/Button';
import { IndStatusModal } from '../../../indication/components/IndicationsScreen/IndicationsScreen';
import { ConfirmationModal, StatusModal } from '../../../common/components/Modal/Modal';
import { I18n } from 'react-redux-i18n';
import { zeroPad } from '../../../utils/various';
import { dispatchContractSave, getAdelantoPostSaveState } from '../../contractSave';
import { PAMI_CONTRACT_ID } from '../PAMINotificationsSection';
import { VOLUME_CONTRACT_TYPE_ID, ADELANTO_CONTRACT_TYPE_ID } from '../../../constants';
import {
    findLatestContratoVersionIndex,
    hasExactlyOneAdelantoBenefit,
    isContractTypeSelectorDisabled,
    isNewContratoVersionStartDateValid,
    resolveSelectedContratoVersion,
} from '../../contractVersion';
import {
    formatAdelantoBenefitLabel,
    formatAdelantoNexAmount,
    getAdelantoCommittedUnits,
    getAdelantoPrimaryBenefit,
    getAdelantoProductLabel,
    getAdelantoProductId,
    getImporteNexCalculado,
} from '../../../adelanto/adelantoNexAmount';
import { hasAdelantoEnviado } from '../../../adelanto/adelantoNexState';

const ContractDetails = ({
    osList,
    indications,
    contract: { id, descripcion, obra_social, tipo_contrato, indicacion },
    updContract,
    contractTypes,
    showErrors,
    isSur,
    isVolume,
    isAdelanto,
    contractTypeId,
    setVersion,
}) => {
    const readOnly = setVersion.cant_txns > 0;
    const contractTypeSelectorDisabled = isContractTypeSelectorDisabled(id, contractTypeId);
    console.log('ContractDetails rendered');

    return (
        <Card
            className='side-padding-m'
            title={I18n.t('contract.cardHeaders.titleContract')}
            detail={id ? I18n.t('contract.cardHeaders.titleNumber') + ': ' + zeroPad(id, 2) : ''}
        >
            <div className='card-row'>
                <div className='cb-mt'>
                    <Input
                        disabled={readOnly}
                        className='white'
                        value={descripcion}
                        handleChange={(v) => updContract({ descripcion: v })}
                        label={I18n.t('contract.inputLabels.name')}
                        error={showErrors && !descripcion}
                    />
                </div>
                <div className='cb-mt'>
                    <div key='input-label'>
                        <label className='input-label'>{I18n.t('contract.inputLabels.os')}</label>
                    </div>
                    <CustomSelect
                        disabled={readOnly}
                        options={osList
                            ?.map((os) => ({
                                value: os?.id,
                                label: os?.razon_social,
                                publicKey: os?.pubkey,
                            }))
                            ?.sort((a, b) => (a.label > b.label ? 1 : -1))}
                        value={obra_social}
                        onSelect={(v) => updContract({ obra_social: v })}
                        placeholder=''
                        error={showErrors && !obra_social}
                        className='rounded i-screen mxh'
                        searchable={true}
                    />
                </div>
                <div className='cb-mt'>
                    <div key='input-label'>
                        <label className='input-label'>
                            {I18n.t('contract.inputLabels.contractType')}
                        </label>
                    </div>
                    <CustomSelect
                        disabled={contractTypeSelectorDisabled}
                        options={contractTypes
                            ?.map((type) => ({
                                value: type.id,
                                label: type.descripcion,
                                key: type.tipo_contrato,
                            }))
                            ?.sort((a, b) =>
                                a.label.toLowerCase() > b.label.toLowerCase() ? 1 : -1,
                            )}
                        value={contractTypeId}
                        onSelect={(newContractTypeId) => {
                            const foundContract = contractTypes.find(
                                (c) => c.id === newContractTypeId,
                            );
                            if (foundContract) {
                                updContract({
                                    tipo_contrato: foundContract.id,
                                    tipo_contrato_descr: foundContract.descripcion,
                                    tipo_contrato_tipo: foundContract.tipo_contrato,
                                });
                            }
                        }}
                        placeholder=''
                        error={showErrors && !tipo_contrato}
                        className='rounded i-screen'
                    />
                </div>
                {!isSur && !isVolume && !isAdelanto && (
                    <div className='cb-mt'>
                        <div key='input-label'>
                            <label className='input-label'>
                                {I18n.t('contract.inputLabels.indications')}
                            </label>
                        </div>
                        <CustomSelect
                            options={indications
                                ?.map((indication) => ({
                                    value: indication?.id,
                                    label: indication?.titulo,
                                    publicKey: `key${indication?.id}`,
                                }))
                                ?.sort((a, b) =>
                                    a.label.toLowerCase() > b.label.toLowerCase() ? 1 : -1,
                                )}
                            value={indicacion}
                            placeholder=''
                            onSelect={(v) => updContract({ indicacion: v, contrato_version: [] })}
                            error={showErrors && !indicacion}
                            disabled={id != undefined || readOnly}
                            className='rounded i-screen mxh'
                            searchable={true}
                        />
                    </div>
                )}
            </div>
        </Card>
    );
};

const Contract = ({
    selectedContract,
    setContractStatus,
    putContract,
    postContract,

    osList,
    getOsList,
    indications,
    getIndications,
    auditorGroups,
    getAuditorGroups,

    selectedIndication,

    // lo mio
    // getFamilyProducts,
    getProdFamily,
    getContractTypes,
    contractTypes,
    product_families,
    setIndicationSelected,
    cleanContractTypes,

    // Adelanto NEX (FR-NEW-02 / FR-03 / FR-04)
    sendAdelanto,
    sendAdelantoStatus,
    resetSendAdelanto,
}) => {
    const [contract, setContract] = useState({});
    const updContract = (obj) => setContract({ ...contract, ...obj });
    const [showState, setShowState] = useState(false);
    const [showErrors, setShowErrors] = useState(false);
    const [selectedVersionIndex, setSelectedVersionIndex] = useState(0);
    const prevContractIdRef = useRef(null);
    const prevVersionCountRef = useRef(0);

    // FR-04 — confirmation/status modal toggles for "Enviar NEX".
    // Kept local because they are pure UI state; the redux slice owns the
    // request lifecycle (sendAdelantoStatus).
    const [showSendConfirm, setShowSendConfirm] = useState(false);
    const [showSendStatus, setShowSendStatus] = useState(false);

    // Post-save "Enviar NEX ahora / más tarde" prompt + the latched flag
    // that survives the postContract/putContract round-trip. We arm the
    // flag synchronously in submitContract and fire the modal from the
    // watcher useEffect below once the new ids land in selectedContract.
    const [showPostSaveAdelantoPrompt, setShowPostSaveAdelantoPrompt] = useState(false);
    const [pendingPostSavePrompt, setPendingPostSavePrompt] = useState(false);
    // True from Adelanto save click until post-save modal is dismissed (or skipped
    // because NEX was already sent). Prevents the header badge flashing while the
    // POST/PUT response is still merging ids into local state.
    const [awaitingAdelantoPostSaveModal, setAwaitingAdelantoPostSaveModal] = useState(false);

    /*
     * FR-03 UX — hide the version-header "Enviar NEX" badge until the
     * operator completes the post-save modal (new contract). Re-opening a
     * saved contract unlocks once ids are in state and we are not in that
     * modal flow (`awaitingAdelantoPostSaveModal` blocks the POST round-trip).
     */
    const [adelantoSendBadgeUnlocked, setAdelantoSendBadgeUnlocked] = useState(false);

    const isSur = contract.tipo_contrato_tipo === 'S';
    const isPami = contract.tipo_contrato === PAMI_CONTRACT_ID;
    const isVolume = contract.tipo_contrato === VOLUME_CONTRACT_TYPE_ID;
    const isAdelanto = contract.tipo_contrato === ADELANTO_CONTRACT_TYPE_ID;

    /*
     * FR-03 — pick the version the "Enviar NEX" button operates on.
     * Contract-screen actions use the version the operator is viewing.
     */
    const selectedAdelantoVersion = resolveSelectedContratoVersion(
        contract?.contrato_version,
        selectedVersionIndex,
        contract?.version_actual,
    );
    const selectedVersion = contract?.contrato_version?.[selectedVersionIndex];
    const selectedVersionLocked =
        !!selectedVersion?.id &&
        (selectedVersion.cant_txns > 0 || (isAdelanto && selectedVersion.adelanto_nex_bloqueado));

    const adelantoAlreadySent = isAdelanto && hasAdelantoEnviado(selectedAdelantoVersion);

    const canSendAdelanto =
        isAdelanto &&
        !!contract?.id &&
        !!selectedAdelantoVersion?.id &&
        getAdelantoCommittedUnits(selectedAdelantoVersion) > 0 &&
        !!getAdelantoProductId(selectedAdelantoVersion) &&
        !!getImporteNexCalculado(selectedAdelantoVersion) &&
        !adelantoAlreadySent &&
        !sendAdelantoStatus?.loading;

    const sendAdelantoDisabledReason = sendAdelantoStatus?.loading
        ? I18n.t('contract.adelantoNex.confirmSendModal.buttonDisabledInFlight')
        : adelantoAlreadySent
        ? I18n.t('contract.adelantoNex.confirmSendModal.buttonDisabledAlreadySent')
        : !contract?.id
        ? I18n.t('contract.adelantoNex.confirmSendModal.buttonDisabledSaveFirst')
        : getAdelantoCommittedUnits(selectedAdelantoVersion) <= 0
        ? I18n.t('contract.adelantoNex.confirmSendModal.buttonDisabledNoVersion')
        : !getAdelantoProductId(selectedAdelantoVersion)
        ? I18n.t('contract.adelantoNex.confirmSendModal.buttonDisabledNoProduct')
        : !getImporteNexCalculado(selectedAdelantoVersion)
        ? I18n.t('contract.adelantoNex.confirmSendModal.buttonDisabledNoImporte')
        : '';

    /*
     * Derived gate for both the persistent yellow banner ("Recuerde enviar
     * NEX...") and the post-save prompt. True iff the contract is a saved
     * Adelanto with a current version whose `adelanto_status` is still
     * NOT_SENT and has committed units. Auto-clears once
     * the contract is refreshed post-send (via the getContract dispatch
     * added in adelanto.middleware.js).
     */
    const adelantoNeedsSend =
        isAdelanto &&
        !!contract?.id &&
        !!selectedAdelantoVersion?.id &&
        getAdelantoCommittedUnits(selectedAdelantoVersion) > 0 &&
        !hasAdelantoEnviado(selectedAdelantoVersion);

    const completeAdelantoPostSaveFlow = () => {
        setShowPostSaveAdelantoPrompt(false);
        setAwaitingAdelantoPostSaveModal(false);
        setAdelantoSendBadgeUnlocked(true);
    };

    const showAdelantoSendBadge =
        isAdelanto &&
        adelantoSendBadgeUnlocked &&
        !awaitingAdelantoPostSaveModal &&
        !showPostSaveAdelantoPrompt &&
        !pendingPostSavePrompt &&
        !!contract?.id &&
        !!selectedAdelantoVersion?.id;

    const adelantoSendSummary = useMemo(() => {
        const ver = selectedAdelantoVersion;
        const primaryBenefit = getAdelantoPrimaryBenefit(ver);
        return {
            product: getAdelantoProductLabel(primaryBenefit, product_families) ?? '—',
            benefit: formatAdelantoBenefitLabel(primaryBenefit),
            committedUnits: getAdelantoCommittedUnits(ver) || '—',
            nexAmount: formatAdelantoNexAmount(
                ver?.adelanto_nex_preparado?.importe_nex ?? getImporteNexCalculado(ver),
            ),
        };
    }, [selectedAdelantoVersion, product_families]);

    const handleConfirmSend = () => {
        const ver = selectedAdelantoVersion;
        if (!ver?.id || !contract?.id) return;

        const producto = getAdelantoProductId(ver);
        if (!producto || !getImporteNexCalculado(ver) || !canSendAdelanto) return;

        sendAdelanto({
            versionId: ver.id,
            contractId: contract.id,
            producto,
            observacion: I18n.t('contract.adelantoNex.confirmSendModal.observacion', {
                version: ver.version ?? ver.id,
            }),
        });
        setShowSendConfirm(false);
        setShowSendStatus(true);
    };

    const closeSendStatus = () => {
        setShowSendStatus(false);
        resetSendAdelanto && resetSendAdelanto();
    };

    const contractReady =
        !!contract.descripcion &&
        !!contract.obra_social &&
        !!contract.tipo_contrato &&
        (!!contract.indicacion || isSur || isVolume || isAdelanto);

    useEffect(() => {
        setContract({
            ...contract,
            ...selectedContract,
            contrato_version:
                selectedContract?.contrato_version?.map((ver) => {
                    const contrato_v_evidencia =
                        ver.contrato_v_evidencia?.map((ev) => ({ ...ev, _id: ev.id })) ?? [];

                    const contrato_v_beneficio = ver.contrato_v_beneficio?.map((ben) => ({
                        ...ben,
                        _id: ben.id,
                        groupId:
                            ben.aplicacion_desde +
                            ' ' +
                            ben.aplicacion_hasta +
                            ' ' +
                            ben.familia_producto,
                    }));

                    contrato_v_beneficio.sort((a, b) => {
                        if (a.aplicacion_desde < b.aplicacion_desde) return -1;
                        if (a.aplicacion_desde > b.aplicacion_desde) return 1;
                        if (a.aplicacion_hasta < b.aplicacion_hasta) return -1;
                        if (a.aplicacion_hasta > b.aplicacion_hasta) return 1;
                        if (a.familia_producto < b.familia_producto) return -1;
                        if (a.familia_producto > b.familia_producto) return 1;
                        if (a.producto < b.producto) return -1;
                        if (a.producto > b.producto) return 1;
                        return 0;
                    });

                    return {
                        ...ver,
                        _id: ver.id,
                        contrato_v_evidencia,
                        contrato_v_beneficio,
                    };
                }) ?? [],
        });
    }, [selectedContract, selectedIndication]);

    /*
     * Keep the version picker aligned on open / "+" but preserve the
     * operator's selection across PUT round-trips (save must not jump back
     * to version_actual while they edit v2/v3).
     */
    useEffect(() => {
        const versions = contract?.contrato_version ?? [];
        if (!versions.length) return;

        if (contract?.id !== prevContractIdRef.current) {
            prevContractIdRef.current = contract?.id ?? null;
            prevVersionCountRef.current = versions.length;
            setSelectedVersionIndex(findLatestContratoVersionIndex(versions));
            return;
        }

        if (versions.length > prevVersionCountRef.current) {
            prevVersionCountRef.current = versions.length;
            setSelectedVersionIndex(versions.length - 1);
            return;
        }

        if (versions.length < prevVersionCountRef.current) {
            prevVersionCountRef.current = versions.length;
        }

        if (selectedVersionIndex >= versions.length) {
            setSelectedVersionIndex(Math.max(0, versions.length - 1));
        }
    }, [contract?.id, contract?.contrato_version?.length, selectedVersionIndex]);

    useEffect(() => {
        cleanContractTypes();
        setIndicationSelected({});
        getOsList();
        // getIndications();
        getAuditorGroups();

        // Lo mio
        //getFamilyProducts();
        getContractTypes();
        getProdFamily();
    }, []);

    /*
     * Adelanto NEX — fire the post-save "Enviar NEX ahora / más tarde"
     * prompt once submitContract has armed `pendingPostSavePrompt` AND
     * the post/put round-trip has materialised a real id on both the
     * contract and the current version. For PUT this lights up on the
     * very next render after dispatch; for POST it lights up once the
     * mock/server response merges into `selectedContract` via the
     * useEffect above. Watching `adelantoNeedsSend` plays defence: if
     * the user saves an Adelanto version that was already sent (status
     * already non-NOT_SENT), the prompt simply never fires.
     */
    useEffect(() => {
        const postSaveState = getAdelantoPostSaveState({
            pending: pendingPostSavePrompt,
            loading: setContractStatus?.loading,
            error: setContractStatus?.error,
            success: setContractStatus?.success,
            contractId: contract?.id,
            versionId: selectedAdelantoVersion?.id,
        });

        if (postSaveState === 'error') {
            setPendingPostSavePrompt(false);
            setAwaitingAdelantoPostSaveModal(false);
            setShowState({
                message: setContractStatus?.message?.toString?.() || I18n.t('errors.generic'),
            });
            return;
        }

        if (postSaveState !== 'success') return;

        if (adelantoNeedsSend) {
            setShowPostSaveAdelantoPrompt(true);
            setPendingPostSavePrompt(false);
            return;
        }

        const alreadySent = hasAdelantoEnviado(selectedAdelantoVersion);

        setPendingPostSavePrompt(false);
        setAwaitingAdelantoPostSaveModal(false);
        if (alreadySent) {
            setAdelantoSendBadgeUnlocked(true);
        } else {
            setShowState({
                message: I18n.t('contract.adelantoNex.saveSuccess'),
            });
        }
    }, [
        selectedContract,
        pendingPostSavePrompt,
        adelantoNeedsSend,
        contract?.id,
        selectedAdelantoVersion?.id,
        selectedAdelantoVersion?.adelantos_nex,
        selectedAdelantoVersion?.adelanto_status,
        setContractStatus?.loading,
        setContractStatus?.success,
        setContractStatus?.error,
        setContractStatus?.message,
    ]);

    useEffect(() => {
        if (!isAdelanto) {
            setAdelantoSendBadgeUnlocked(false);
            setAwaitingAdelantoPostSaveModal(false);
            return;
        }
        const canShowPersistedBadge =
            contract?.id &&
            selectedAdelantoVersion?.id &&
            !awaitingAdelantoPostSaveModal &&
            !showPostSaveAdelantoPrompt &&
            !pendingPostSavePrompt;

        if (canShowPersistedBadge) {
            setAdelantoSendBadgeUnlocked(true);
        }
    }, [
        isAdelanto,
        contract?.id,
        selectedAdelantoVersion?.id,
        awaitingAdelantoPostSaveModal,
        showPostSaveAdelantoPrompt,
        pendingPostSavePrompt,
    ]);

    const submitContract = (type) => {
        // Debug logging for volume contracts
        console.log('🔍 Contract Debug:', {
            isVolume,
            contract_id: contract.id,
            contract_versions: contract?.contrato_version?.map((ver) => ({
                id: ver.id,
                cant_txns: ver.cant_txns,
                version: ver.version,
            })),
        });

        // Check if any version has transactions (cant_txns > 0)
        // const hasTransactions = contract?.contrato_version?.some((ver) => ver.cant_txns > 0);
        // if (hasTransactions) {
        //     setShowState({
        //         message:
        //             'No se puede modificar un contrato que tiene versiones con transacciones registradas. Solo se permite inactivar las versiones.',
        //     });
        //     return;
        // }

        // Adelanto NEX contract-level pre-validation: OS must have an
        // active + granted wallet (BA-02 enforces the same on the backend).
        // Skipped for non-Adelanto contract types.
        if (isAdelanto) {
            const selectedOs = osList?.find((o) => o.id === contract.obra_social);
            const hasActiveWallet = !!selectedOs?.addresses?.some((a) => a.activo && a.granted);
            if (!hasActiveWallet) {
                setShowState({
                    message: I18n.t('contract.validationErrors.osWalletRequired'),
                });
                return;
            }
        }

        let vCounter = 0;
        const durationString = I18n.t(
            'indication.details.indication.durationTypes.' + selectedIndication?.tipo_duracion,
        );
        const versionRows = contract?.contrato_version ?? [];
        for (let versionIndex = 0; versionIndex < versionRows.length; versionIndex++) {
            const ver = versionRows[versionIndex];
            const skipAdelantoValidation =
                isAdelanto &&
                (ver.estado === 'B' || (!ver.id && versionIndex > selectedVersionIndex));
            if (
                !ver.id &&
                ver.vigencia_desde &&
                !isNewContratoVersionStartDateValid(
                    ver.vigencia_desde,
                    versionRows.filter((_, index) => index !== versionIndex),
                )
            ) {
                setShowState({
                    message: `Error en las fechas en version ${
                        versionIndex + 1
                    }.\n Debe seleccionar una fecha de vigencia igual o posterior a la fecha mínima permitida`,
                });
                return;
            }

            if (skipAdelantoValidation) {
                continue;
            }

            console.log('------------contract_version:', ver);
            // TEMPORAL --- TODO : Delete
            if (isVolume || isAdelanto) ver.fin_incorp_pacientes = ver.vigencia_hasta;
            // TODO : DELETE
            vCounter++;
            let gId = 'inv_gid';
            let dateCounter = -1;
            if (!ver.vigencia_desde) {
                setShowState({
                    message: `Error en las fechas en version ${vCounter}.\n Debe seleccionar la fecha de inicio de vigencia`,
                });
                return;
            }
            if (!isVolume && !isAdelanto && !ver.fin_incorp_pacientes) {
                setShowState({
                    message: `Error en las fechas en version ${vCounter}.\n Debe seleccionar la fecha de fin de alta de pacientes`,
                });
                return;
            }
            if (!ver.vigencia_hasta) {
                setShowState({
                    message: `Error en las fechas en version ${vCounter}.\n Debe seleccionar la fecha de fin de registracion`,
                });
                return;
            }
            if (!isVolume && !isAdelanto && (!ver.max_pacientes || ver.max_pacientes <= 0)) {
                setShowState({
                    message: `Error en maxima cantidad de pacientes en version ${vCounter}.\n La maxima cantidad de pacientes debe ser mayor a 0`,
                });
                return;
            }
            if (!isVolume && !isAdelanto && contract.tipo_contrato_tipo !== 'S') {
                if (ver.tolerancia_periodo == null) {
                    setShowState({
                        message: `Error en tolerancia en version ${vCounter}.\n Debe seleccionar una tolerancia (periodo de control)`,
                    });
                    return;
                }
                if (ver.tolerancia_dosis == null) {
                    setShowState({
                        message: `Error en tolerancia en version ${vCounter}.\n Debe seleccionar una tolerancia (dosis mg)`,
                    });
                    return;
                }
                if (ver.tolerancia_dosis > 100) {
                    setShowState({
                        message: `Error en tolerancia en version ${vCounter}.\n La tolerancia no puede ser mayor a 100%`,
                    });
                    return;
                }
                if (!ver.grupo_auditor) {
                    setShowState({
                        message: `Error en auditor en version ${vCounter}.\n Debe seleccionar un grupo de auditores`,
                    });
                    return;
                }
                if (!ver.compartir_nya && !ver.compartir_dni && !ver.compartir_afiliado) {
                    setShowState({
                        message: `Error en auditor en version ${vCounter}.\n Debe seleccionar al menos un campo a compartir con el auditor`,
                    });
                    return;
                }
            }

            // Volume contract specific validations
            if (isVolume) {
                if (!ver.unidades_por_periodo || ver.unidades_por_periodo <= 0) {
                    setShowState({
                        message: `Error en unidades por periodo en version ${vCounter}.\n Debe ingresar un valor mayor a 0`,
                    });
                    return;
                }
                if (!ver.dias_vto_lst_precios || ver.dias_vto_lst_precios <= 0) {
                    setShowState({
                        message: `Error en dias de vencimiento de lista de precios en version ${vCounter}.\n Debe ingresar un valor mayor a 0`,
                    });
                    return;
                }
            }

            // Adelanto NEX contract specific validations
            if (isAdelanto) {
                if (!ver.unidades_por_periodo || ver.unidades_por_periodo <= 0) {
                    setShowState({
                        message: `Error en unidades por compra en version ${vCounter}.\n Debe ingresar un valor mayor a 0`,
                    });
                    return;
                }
                ver.contrato_v_evidencia = [];
            }
            if (!isAdelanto) {
                for (const ben of ver.contrato_v_beneficio ?? []) {
                    if (ben.groupId !== gId) {
                        gId = ben.groupId;
                        if (
                            ben.aplicacion_desde < dateCounter ||
                            ben.aplicacion_hasta < ben.aplicacion_desde
                        ) {
                            setShowState({
                                message: `Error en ${durationString} de productos en version ${vCounter}.\n ${durationString} superpuestas o invalidas`,
                            });
                            return;
                        }
                        if (isNaN(ben.beneficio)) {
                            setShowState({
                                message: `Error en el beneficio de productos en version ${vCounter}.\n Debe ingresar el beneficio`,
                            });
                            return;
                        }
                        if (
                            ben.aplicacion_desde > selectedIndication.duracion ||
                            ben.aplicacion_hasta > selectedIndication.duracion
                        ) {
                            setShowState({
                                message: `Error en el beneficio de productos en version ${vCounter}.\n Fuera de rango`,
                            });
                            return;
                        }
                        dateCounter = ben.aplicacion_hasta;
                    }
                    if (ben.tipo_beneficio !== 'F' && !ben.producto_ref) {
                        setShowState({
                            message: `Error en el beneficio de productos en version ${vCounter}.\n Debe ingresar producto de referencia`,
                        });
                        return;
                    }
                }
            } else {
                if (!hasExactlyOneAdelantoBenefit(ver)) {
                    setShowState({
                        message: `Error en beneficios en version ${vCounter}.\n Debe configurar exactamente un beneficio`,
                    });
                    return;
                }
            }

            if (!isAdelanto && ver.contrato_v_evidencia?.some((x) => !x.tipo_evidencia)) {
                setShowState({
                    message: `Error en evidencias de la version ${vCounter}.\n Error evidencia sin tipo de descripción.\n`,
                });
                return;
            }

            if (!isAdelanto && ver.contrato_v_evidencia?.some((x) => !x.descripcion)) {
                setShowState({
                    message: `Error en evidencias de la version ${vCounter}.\n Error evidencia sin descripción.\n`,
                });
                return;
            }

            if (!isAdelanto) {
                const search = ver.contrato_v_evidencia?.reduce((acc, ind) => {
                    acc[ind.aplicacion] = ++acc[ind.aplicacion] || 0;
                    return acc;
                }, {});

                const duplicates = ver.contrato_v_evidencia?.filter((ind) => {
                    return search[ind.aplicacion];
                });

                if (duplicates?.length > 0) {
                    setShowState({
                        message: `Error en evidencias de la version ${vCounter}.\n Error evidencias con mismo ciclo.\n`,
                    });
                    return;
                }
                if (
                    ver.contrato_v_evidencia?.some(
                        (x) => x.aplicacion > selectedIndication.duracion,
                    )
                ) {
                    setShowState({
                        message: `Error en evidencias de la version ${vCounter}.\n Error evidencia con ciclo fuera de rango. El límite es ${selectedIndication?.duracion}.`,
                    });
                    return;
                }
            }

            const notificationsSomeNull =
                ver.notificaciones && Object.values(ver.notificaciones).some((n) => n === null);
            if (isPami && notificationsSomeNull) {
                setShowState({
                    message: `Debe ingresar un valor en Notificaciones de la version ${vCounter}.`,
                });
                return;
            }

            const evidenceMissingDays =
                !isAdelanto && ver.contrato_v_evidencia.slice(1, 3).some((e) => !e.dias);
            if (isPami && evidenceMissingDays) {
                setShowState({
                    message: `Los dias de las evidencias de la version ${vCounter} deben ser mayores a 0`,
                });
                return;
            }
        }

        const contractToSave = isAdelanto
            ? {
                  ...contract,
                  contrato_version: versionRows.filter((v, i) => {
                      if (v?.estado === 'B' && v?.id) return true;
                      if (!v?.id && i > selectedVersionIndex) return false;
                      return true;
                  }),
              }
            : contract;

        dispatchContractSave({ type, contract: contractToSave, postContract, putContract });

        if (isAdelanto) {
            /*
             * Adelanto NEX — arm the post-save "Enviar NEX ahora / más
             * tarde" prompt. The watcher useEffect above fires the modal
             * once the round-trip lands and `adelantoNeedsSend` is true.
             *
             * We deliberately do NOT call `setShowState({message:
             * undefined})` here: that fires IndStatusModal, which shares
             * z-index 999 with our prompt, renders later in the tree (so
             * it stacks on top), and on its "Ok" click does
             * `history.push('/contracts/list')` — which would unmount
             * this page before the user can choose between "Enviar NEX
             * ahora" and "más tarde". Validation errors above still use
             * setShowState with a real `message`, so the global error
             * modal continues to work for the failure paths.
             */
            setAdelantoSendBadgeUnlocked(false);
            setAwaitingAdelantoPostSaveModal(true);
            setPendingPostSavePrompt(true);
        } else {
            setShowState({ message: undefined });
        }
    };

    console.log('------------isVolume:', isVolume);
    console.log('------------contract:', contract.tipo_contrato);

    const hasUnsavedAdelantoVersion = (contract?.contrato_version ?? []).some((v) => !v?.id);

    const adelantoEstadoInactivationPending = useMemo(() => {
        if (!isAdelanto || !contract?.id || !selectedContract?.contrato_version) {
            return false;
        }
        const persisted = selectedContract.contrato_version;
        return (contract.contrato_version ?? []).some((ver) => {
            if (!ver?.id) return false;
            const orig = persisted.find((o) => o.id === ver.id);
            if (!orig) return false;
            const wasLocked = orig.cant_txns > 0 || hasAdelantoEnviado(orig);
            return wasLocked && ver.estado !== orig.estado;
        });
    }, [isAdelanto, contract?.id, contract?.contrato_version, selectedContract?.contrato_version]);

    const allAdelantoVersionsLocked =
        isAdelanto &&
        !hasUnsavedAdelantoVersion &&
        !adelantoEstadoInactivationPending &&
        (() => {
            const activeVersions = (contract?.contrato_version ?? []).filter(
                (v) => v?.estado !== 'B',
            );
            return (
                activeVersions.length > 0 &&
                activeVersions.every((v) => v.cant_txns > 0 || hasAdelantoEnviado(v))
            );
        })();

    return (
        <div className='i-container'>
            <ContractDetails
                osList={osList}
                indications={indications}
                contract={contract}
                updContract={updContract}
                showErrors={showErrors}
                contractTypes={contractTypes}
                isSur={isSur}
                isVolume={isVolume}
                isAdelanto={isAdelanto}
                contractTypeId={contract.tipo_contrato}
                setVersion={{ cant_txns: selectedVersionLocked ? 1 : 0 }}
            />
            {adelantoNeedsSend && (
                <div className='adelanto-pending-banner'>
                    <span className='adelanto-pending-banner__text'>
                        {I18n.t('contract.adelantoNex.banner.pendingSend')}
                    </span>
                    <Button
                        type='button'
                        className='primary small adelanto-pending-banner__btn'
                        text={I18n.t('contract.adelantoNex.banner.sendNow')}
                        onClick={() => submitContract('put')}
                        disabled={selectedVersionLocked}
                    />
                </div>
            )}
            {contractReady && (
                <VersionContainer
                    indication={contract.indicacion}
                    versions={contract.contrato_version}
                    updVersions={(vers) => updContract({ contrato_version: vers })}
                    auditorGroups={auditorGroups}
                    showErrors={showErrors}
                    selectedIndication={selectedIndication}
                    getIndications={getIndications}
                    isSur={isSur}
                    isVolume={isVolume}
                    isPami={isPami}
                    isAdelanto={isAdelanto}
                    lastVersion={contract.version_actual}
                    typeContractSelected={contract.tipo_contrato}
                    currentVersion={selectedVersionIndex}
                    onCurrentVersionChange={setSelectedVersionIndex}
                    showAdelantoSendBadge={showAdelantoSendBadge}
                />
            )}

            {contract?.id ? (
                <Button
                    type='button'
                    className='primary small f-right'
                    text={I18n.t('contract.buttons.save')}
                    onClick={() => submitContract('put')}
                    disabled={selectedVersionLocked || allAdelantoVersionsLocked}
                />
            ) : (
                <Button
                    type='button'
                    className='primary small f-right'
                    text={I18n.t('contract.buttons.new')}
                    onClick={() => submitContract('post')}
                />
            )}

            {/* FR-03 — "Enviar NEX" CTA. Originally rendered here next to
                Guardar Cambios; we moved it into the version header where it
                doubles as the AdelantoStatusBadge (see VersionContainer in
                Version.jsx). Kept commented for one beat in case we want to
                revert to a duplicate placement after demo feedback. */}
            {false && isAdelanto && (
                <Button
                    type='button'
                    className='secondary small f-right adelanto-send-btn'
                    text={I18n.t('contract.versionDetail.buttons.sendNex')}
                    onClick={() => setShowSendConfirm(true)}
                    disabled={!canSendAdelanto}
                    title={sendAdelantoDisabledReason}
                />
            )}

            {/* FR-04 — confirmation modal. Surfaces a brief summary of the
                version about to be advanced so Roche acknowledges the
                irreversibility before triggering the BA-05 endpoint. */}
            <ConfirmationModal
                show={showSendConfirm}
                title={I18n.t('contract.adelantoNex.confirmSendModal.title')}
                submit={handleConfirmSend}
                cancel={() => setShowSendConfirm(false)}
                submitText={I18n.t('contract.adelantoNex.confirmSendModal.confirm')}
                cancelText={I18n.t('contract.adelantoNex.confirmSendModal.cancel')}
                close={() => setShowSendConfirm(false)}
                loading={sendAdelantoStatus?.loading}
                disableSubmitButton={sendAdelantoStatus?.loading}
            >
                <div className='adelanto-confirm-body'>
                    <p>{I18n.t('contract.adelantoNex.confirmSendModal.description')}</p>
                    <ul className='adelanto-confirm-summary'>
                        <li>
                            <strong>
                                {I18n.t('contract.adelantoNex.confirmSendModal.version')}:
                            </strong>{' '}
                            {selectedAdelantoVersion?.version ?? '—'}
                        </li>
                        <li>
                            <strong>
                                {I18n.t('contract.adelantoNex.confirmSendModal.product')}:
                            </strong>{' '}
                            {adelantoSendSummary.product}
                        </li>
                        <li>
                            <strong>
                                {I18n.t('contract.adelantoNex.confirmSendModal.benefit')}:
                            </strong>{' '}
                            {adelantoSendSummary.benefit}
                        </li>
                        <li>
                            <strong>
                                {I18n.t('contract.adelantoNex.confirmSendModal.committedUnits')}:
                            </strong>{' '}
                            {adelantoSendSummary.committedUnits}
                        </li>
                        <li>
                            <strong>
                                {I18n.t('contract.adelantoNex.confirmSendModal.amount')}:
                            </strong>{' '}
                            {adelantoSendSummary.nexAmount} NEX
                        </li>
                    </ul>
                </div>
            </ConfirmationModal>

            {/* FR-04 (cont.) — post-send status feedback. Reads from the
                Adelanto redux slice; stays on the contract detail page on
                success so the user can see badges/state update (FR-05). */}
            <StatusModal
                show={showSendStatus}
                close={closeSendStatus}
                status={sendAdelantoStatus}
                successMessage={I18n.t('contract.adelantoNex.confirmSendModal.successMessage')}
            />

            {/* Post-save prompt — fired by the watcher useEffect once
                an Adelanto save round-trip resolves and a NOT_SENT
                current version with positive units is in scope. Lets the
                operator either jump straight to the send flow or defer
                and rely on the persistent banner above. */}
            <ConfirmationModal
                show={showPostSaveAdelantoPrompt}
                title={I18n.t('contract.adelantoNex.postSaveModal.title')}
                submit={() => {
                    completeAdelantoPostSaveFlow();
                    setShowSendConfirm(true);
                }}
                cancel={completeAdelantoPostSaveFlow}
                submitText={I18n.t('contract.adelantoNex.postSaveModal.sendNow')}
                cancelText={I18n.t('contract.adelantoNex.postSaveModal.sendLater')}
                close={completeAdelantoPostSaveFlow}
            >
                <div className='adelanto-confirm-body'>
                    <p>{I18n.t('contract.adelantoNex.postSaveModal.body')}</p>
                </div>
            </ConfirmationModal>

            <IndStatusModal
                show={showState}
                setShow={setShowState}
                status={setContractStatus}
                setShowErrors={setShowErrors}
            />
        </div>
    );
};

export default Contract;
